import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { AnimationVariant } from '@/services/enums';

interface ScrollRevealProps {
  children: ReactNode;
  animation?: AnimationVariant;
  delay?: number;
  duration?: number;
  threshold?: number;
  className?: string;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  animation = 'fadeInUp',
  delay = 0,
  duration = 600,
  threshold = 0.1,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // 提前50px触发
      },
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [delay, threshold]);

  const getAnimationClass = () => {
    const baseClass = 'transition-all ease-out';

    if (!isVisible) {
      switch (animation) {
        case 'fadeInUp':
          return `${baseClass} opacity-0 translate-y-8`;
        case 'fadeInDown':
          return `${baseClass} opacity-0 -translate-y-8`;
        case 'fadeInLeft':
          return `${baseClass} opacity-0 -translate-x-8`;
        case 'fadeInRight':
          return `${baseClass} opacity-0 translate-x-8`;
        case 'scaleIn':
          return `${baseClass} opacity-0 scale-95`;
        case 'slideIn':
          return `${baseClass} opacity-0 translate-y-4`;
        default:
          return `${baseClass} opacity-0 translate-y-8`;
      }
    }

    switch (animation) {
      case 'fadeInUp':
        return `${baseClass} opacity-100 translate-y-0`;
      case 'fadeInDown':
        return `${baseClass} opacity-100 translate-y-0`;
      case 'fadeInLeft':
        return `${baseClass} opacity-100 translate-x-0`;
      case 'fadeInRight':
        return `${baseClass} opacity-100 translate-x-0`;
      case 'scaleIn':
        return `${baseClass} opacity-100 scale-100`;
      case 'slideIn':
        return `${baseClass} opacity-100 translate-y-0`;
      default:
        return `${baseClass} opacity-100 translate-y-0`;
    }
  };

  return (
    <div
      ref={elementRef}
      className={`${getAnimationClass()} ${className}`}
      style={{
        transitionDuration: `${duration}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
